// src/app/modules/feature-toggle/guards/feature.guard.ts
import { Injectable } from '@angular/core';
import {
  CanActivate,
  ActivatedRouteSnapshot,
  Router,
  UrlTree,
} from '@angular/router';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { SubscriptionService } from '../services/subscription.service';
import { Feature } from '../enums/features.enum';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { PlatformService } from 'src/app/core/services/platform/platform.service';
import { ClientLoggerService } from 'src/app/core/services/logging/client-logger.service';
import { LogCategory } from 'src/app/core/services/logging/client-log.models';

@Injectable({
  providedIn: 'root',
})
export class FeatureGuard implements CanActivate {
  constructor(
    private subscriptionService: SubscriptionService,
    private router: Router,
    private toastr: ToastrService,
    private translate: TranslateService,
    private platformService: PlatformService,
    private logger: ClientLoggerService
  ) {}

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    const requiredFeatures = (route.data['features'] as Feature[]) || [];
    const requireAll = route.data['requireAll'] ?? true;
    // On iOS: Redirect to dashboard instead of subscription page (App Store Guideline 3.1.1)
    const defaultRedirect = this.platformService.isIos() ? '/dashboard' : '/settings/subscription';
    const redirectTo = route.data['redirectTo'] ?? defaultRedirect;

    if (!requiredFeatures || requiredFeatures.length === 0) {
      return this.subscriptionService.featuresLoaded$.pipe(map(() => true));
    }

    // Warte auf Feature-Laden, dann prüfe
    return this.subscriptionService.ensureFeaturesLoaded().pipe(
      map((featuresLoaded) => {
        // „Nicht geladen" ist NICHT „nicht im Abo". Scheitert der Subscription-Call (Backend
        // kurz weg, kalter Start), ist das Feature-Set leer — früher lief die Prüfung
        // trotzdem und sperrte mit Toast + Redirect auf die Abo-Seite. Das traf genau den
        // ERSTEN Login nach frischem Storage (danach deckt der localStorage-Cache
        // `company_features` den Fall ab), sah für Nutzer also wie ein Zufallsfehler aus.
        // Hier bewusst fail-open: das Backend erzwingt das Gate serverseitig ohnehin
        // (Aufgaben z. B. mit 403 TASK_FEATURE_DISABLED).
        if (!featuresLoaded) {
          this.logger.logWarning(
            'FeatureGuard: Feature-Status unbekannt (Abo-Daten nicht geladen) - Route wird durchgelassen',
            LogCategory.System,
            {
              componentName: 'FeatureGuard',
              operation: 'feature-status-unknown',
              requiredFeatures: requiredFeatures.map((f) => Feature[f]).join(', '),
              url: route.url.map((s) => s.path).join('/'),
            }
          );
          return true;
        }

        const hasAccess = requireAll
          ? this.subscriptionService.hasAllFeatures(...requiredFeatures)
          : this.subscriptionService.hasAnyFeature(...requiredFeatures);

        if (!hasAccess) {
          // Feature-Namen für die Nachricht sammeln
          const featureNames = requiredFeatures
            .map(
              (f) =>
                this.translate.instant(`FEATURES.${Feature[f]}`) || Feature[f]
            )
            .join(', ');

          this.toastr.warning(
            // Platzhalter heißt `feature` (Singular) — wie an allen anderen Aufrufstellen
            // von FEATURES.UPGRADE_REQUIRED_FOR. Mit `features` blieb „{{feature}}" roh
            // im Toast stehen.
            this.translate.instant('FEATURES.UPGRADE_REQUIRED_FOR', {
              feature: featureNames,
            }),
            this.translate.instant('FEATURES.ACCESS_DENIED')
          );

          return this.router.createUrlTree([redirectTo]);
        }

        return true;
      })
    );
  }
}
